/**
 * A pane that shows something larger than itself: fit, fit width, 100 %, zoom.
 *
 * Figures from the reporting stage are a mixed lot. A four-column slice montage
 * is ten times taller than it is wide, and fitting the whole of it into the
 * pane turns thirty rows of brain into a strip nobody can read. So the first
 * view is chosen from the shape: fit the whole thing when that keeps it
 * reasonably large, otherwise fit the width and let it scroll.
 *
 * The content reports its size at 100 %, and is drawn at whatever scale the
 * pane settles on. Ctrl/⌘ + wheel zooms about the pointer; dragging pans.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { Maximize, MoveHorizontal, ZoomIn, ZoomOut } from "lucide-react";

import { Button } from "@/components/ui/primitives";
import { cn } from "@/lib/utils";

type Mode = "fit" | "width" | "manual";

const MIN_SCALE = 0.05;
const MAX_SCALE = 8;
const PAD = 12;
/** Fitting the whole figure is the default unless it would be drawn this much
 *  smaller than fitting its width — the tall-montage case. */
const FIT_WHOLE_IF_AT_LEAST = 0.6;

const clamp = (s: number) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, s));

export function ZoomPane({
  width,
  height,
  children,
  initial = "auto",
  className,
}: {
  /** Content size at 100 %, in CSS pixels. 0 while unknown. */
  width: number;
  height: number;
  children: (scale: number) => ReactNode;
  initial?: "auto" | "fit" | "width";
  className?: string;
}) {
  const box = useRef<HTMLDivElement>(null);
  const [pane, setPane] = useState({ w: 0, h: 0 });
  const [mode, setMode] = useState<Mode>("fit");
  const [manual, setManual] = useState(1);
  const chosen = useRef(false);
  /** Scrollbar thickness, measured once one has been seen; 16 px until then. */
  const bar = useRef(16);
  const setBar = (px: number) => {
    if (px > 0) bar.current = px;
  };

  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    // offsetWidth: the pane's width with its scrollbar, which does not change
    // when a scrollbar appears. clientWidth does, and fitting to it jitters.
    const measure = () => {
      setPane({ w: el.offsetWidth, h: el.offsetHeight });
      setBar(el.offsetWidth - el.clientWidth || bar.current);
    };
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    measure();
    return () => ro.disconnect();
  }, []);

  // Fit width scrolls vertically, so the vertical scrollbar takes its share.
  const rawWidth = width > 0 && pane.w > 0 ? (pane.w - 2 * PAD) / width : 1;
  const scrolls = height * rawWidth + 2 * PAD > pane.h;
  const fitWidth = width > 0 && pane.w > 0 ? (pane.w - 2 * PAD - (scrolls ? bar.current : 0)) / width : 1;
  const fitWhole = width > 0 && pane.h > 0 ? Math.min(rawWidth, (pane.h - 2 * PAD) / height) : 1;

  // New content: pick the first view once its size and the pane's are known.
  useEffect(() => {
    chosen.current = false;
  }, [width, height]);
  useEffect(() => {
    if (chosen.current || width <= 0 || pane.w <= 0) return;
    chosen.current = true;
    const pick =
      initial === "auto" ? (fitWhole >= FIT_WHOLE_IF_AT_LEAST * fitWidth ? "fit" : "width") : initial;
    setMode(pick);
  }, [width, height, pane.w, pane.h, fitWhole, fitWidth, initial]);

  const scale = clamp(mode === "fit" ? fitWhole : mode === "width" ? fitWidth : manual);

  /** Zoom to `next`, keeping the content point under (cx, cy) where it is. */
  const zoomTo = useCallback(
    (next: number, cx?: number, cy?: number) => {
      const el = box.current;
      next = clamp(next);
      if (!el) return;
      const px = cx ?? el.clientWidth / 2;
      const py = cy ?? el.clientHeight / 2;
      const ratio = next / scale;
      const sx = (el.scrollLeft + px) * ratio - px;
      const sy = (el.scrollTop + py) * ratio - py;
      setManual(next);
      setMode("manual");
      requestAnimationFrame(() => {
        el.scrollLeft = sx;
        el.scrollTop = sy;
      });
    },
    [scale],
  );

  // Ctrl/⌘ + wheel zooms (a trackpad pinch arrives as one too); a plain wheel
  // scrolls as usual. Non-passive, or preventDefault is ignored.
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      const r = el.getBoundingClientRect();
      zoomTo(scale * Math.exp(-e.deltaY * 0.0015), e.clientX - r.left, e.clientY - r.top);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [scale, zoomTo]);

  // Drag to pan, once the content is larger than the pane.
  const drag = useRef<{ x: number; y: number; l: number; t: number } | null>(null);
  const onPointerDown = (e: React.PointerEvent) => {
    const el = box.current;
    if (!el || e.button !== 0) return;
    drag.current = { x: e.clientX, y: e.clientY, l: el.scrollLeft, t: el.scrollTop };
    el.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const el = box.current;
    const d = drag.current;
    if (!el || !d) return;
    el.scrollLeft = d.l - (e.clientX - d.x);
    el.scrollTop = d.t - (e.clientY - d.y);
  };
  const endDrag = () => {
    drag.current = null;
  };

  const w = width * scale;
  const h = height * scale;

  return (
    <div className={cn("flex h-full flex-col", className)}>
      <div className="flex shrink-0 items-center gap-1 border-b border-ads-border px-2 py-1">
        <Button size="sm" variant={mode === "fit" ? "outline" : "ghost"} onClick={() => setMode("fit")} title="Fit the whole figure">
          <Maximize className="h-3 w-3" />
          Fit
        </Button>
        <Button size="sm" variant={mode === "width" ? "outline" : "ghost"} onClick={() => setMode("width")} title="Fit the width and scroll">
          <MoveHorizontal className="h-3 w-3" />
          Fit width
        </Button>
        <Button size="sm" variant="ghost" onClick={() => zoomTo(1)} title="Actual size">
          100%
        </Button>
        <span className="mx-1 h-4 w-px bg-ads-border" />
        <Button size="sm" variant="ghost" onClick={() => zoomTo(scale / 1.25)} title="Zoom out" aria-label="Zoom out">
          <ZoomOut className="h-3 w-3" />
        </Button>
        <span className="w-12 text-center text-[11px] tabular-nums text-neutral-600">{Math.round(scale * 100)}%</span>
        <Button size="sm" variant="ghost" onClick={() => zoomTo(scale * 1.25)} title="Zoom in" aria-label="Zoom in">
          <ZoomIn className="h-3 w-3" />
        </Button>
        <span className="ml-auto text-[10.5px] text-neutral-400">Ctrl + wheel to zoom · drag to pan</span>
      </div>
      <div
        ref={box}
        className={cn(
          "min-h-0 flex-1 select-none overflow-auto bg-ads-viewer",
          w > pane.w || h > pane.h ? "cursor-grab active:cursor-grabbing" : "",
        )}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        {/* Centred while smaller than the pane, scrollable from the top-left
            corner once larger — `margin: auto` in a grid does both. */}
        <div className="grid min-h-full min-w-full" style={{ padding: PAD, width: w + 2 * PAD, height: h + 2 * PAD }}>
          <div className="m-auto" style={{ width: w, height: h }}>
            {width > 0 ? children(scale) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
